import React, { useState, useEffect, useContext } from 'react';  
import axios from 'axios';
import '../../../assests/css/customer/UserAccountSetting.css';
import UserMasterLayout from '../../../Layouts/UserMasterLayout/UserMasterLayout';
import Swal from 'sweetalert2';
import { Shield, Bell, ShoppingBag, CreditCard, Lock } from 'lucide-react';
import UserAddress from '../../Address/UserAddress';
import UserPayments from '../UserPayments/UserPayments';
import AppContext from '../../../context/AppContext';  

const UserAccountSettings = () => {
  const { baseUrl, authToken } = useContext(AppContext);  
  const authHeaders = { headers: { Authorization: `Bearer ${authToken}` } };

  const [activeTab, setActiveTab] = useState('notifications');
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    inAppNotifications: false
  });

  useEffect(() => {
    if (!authToken) return;
    axios.get(`${baseUrl}/customer/notification-prefs`, authHeaders)
      .then(res => {
        setNotifications({
          emailNotifications: res.data.email_notifications,
          inAppNotifications: res.data.in_app_notifications,
        });
      })
      .catch(err => {
        console.log('Notification prefs fetch error:', err);
      });
  }, [authToken]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNotificationToggle = (key) => {
    setNotifications({
      ...notifications,
      [key]: !notifications[key]
    });
  };

  const handlePasswordUpdate = () => {
    if (!formData.currentPassword) {
      Swal.fire({
        title: 'Missing Field',
        text: 'Please enter your current password',
        icon: 'warning',
        confirmButtonColor: '#E3CAA5'
      });
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      Swal.fire({
        title: 'Password Mismatch',
        text: 'New passwords do not match!',
        icon: 'error',
        confirmButtonColor: '#E3CAA5'
      });
      return;
    }
    if (formData.newPassword.length < 12) {
      Swal.fire({
        title: 'Weak Password',
        text: 'Password must be at least 12 characters long!',
        icon: 'warning',
        confirmButtonColor: '#E3CAA5'
      });
      return;
    }
    Swal.fire({
      title: 'Success!',
      text: 'Password updated successfully!',
      icon: 'success',
      confirmButtonColor: '#E3CAA5'
    });
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleCancelPassword = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    Swal.fire({
      title: 'Cancelled',
      text: 'Password change cancelled',
      icon: 'info',
      confirmButtonColor: '#E3CAA5'
    });
  };

  
  const handleSavePreferences = () => {
    axios.post(`${baseUrl}/customer/notification-prefs`, {
      email_notifications: notifications.emailNotifications,
      in_app_notifications: notifications.inAppNotifications,
    }, authHeaders)
      .then(() => {
        Swal.fire({
          title: 'Saved!',
          text: 'Notification preferences saved successfully!',
          icon: 'success',
          confirmButtonColor: '#E3CAA5'
        });
      })
      .catch(() => {
        Swal.fire({
          title: 'Error',
          text: 'Preferences does not saved ,try again ',
          icon: 'error',
          confirmButtonColor: '#E3CAA5'
        });
      });
  };

  const renderSecurityPassword = () => (
    <div className="as-content-wrapper">
      <div className="as-password-section-centered">
        <h3 className="as-section-title">Change Password</h3>
        <p className="as-section-description">
          Ensure your account is using a long, random password to stay secure.
        </p>

        <div className="as-password-form">
          <div className="as-form-group">
            <label className="as-label">
              <Lock size={16} />
              Current Password
            </label>
            <input
              type="password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleInputChange}
              className="as-input"
              placeholder="••••••••"
            />
          </div>

          <div className="as-form-group">
            <label className="as-label">
              <Lock size={16} />
              New Password
            </label>
            <input
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              className="as-input"
              placeholder="••••••••"
            />
            <span className="as-hint-text">At least 12 characters, including numbers and symbols.</span>
          </div>

          <div className="as-form-group">
            <label className="as-label">
              <Lock size={16} />
              Confirm New Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="as-input"
              placeholder="••••••••"
            />
          </div>

          <div className="as-password-buttons">
            <button className="as-btn-primary" onClick={handlePasswordUpdate}>Update Password</button>
            <button className="as-btn-secondary" onClick={handleCancelPassword}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="as-content-wrapper">
      <div className="as-notification-section">
        <div className="as-notification-header">
          <div className="as-bell-icon">
            <Bell size={24} />
          </div>
          <div>
            <h3 className="as-section-title">Notification Preferences</h3>
            <p className="as-section-description">Control how you hear from us.</p>
          </div>
        </div>

        <div className="as-notification-group">
          <h4 className="as-group-title">EMAIL NOTIFICATIONS</h4>
          
          <div className="as-notification-item-new">
            <div>
              <h5 className="as-item-title">Email Notifications</h5>
              <p className="as-item-description">Receive emails about order updates, promotions, and account activity.</p>
            </div>
            <div className="as-status-buttons">
              <button 
                className={`as-status-btn ${notifications.emailNotifications ? 'as-status-active' : ''}`}
                onClick={() => setNotifications({...notifications, emailNotifications: true})}
              >
                Active
              </button>
              <button 
                className={`as-status-btn ${!notifications.emailNotifications ? 'as-status-inactive' : ''}`}
                onClick={() => setNotifications({...notifications, emailNotifications: false})}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>

        <div className="as-notification-group">
          <h4 className="as-group-title">IN-APP NOTIFICATIONS</h4>
          
          <div className="as-notification-item-new">
            <div>
              <h5 className="as-item-title">In-App Notifications</h5>
              <p className="as-item-description">Get notified within the app about important updates and activities.</p>
            </div>
            <div className="as-status-buttons">
              <button 
                className={`as-status-btn ${notifications.inAppNotifications ? 'as-status-active' : ''}`}
                onClick={() => setNotifications({...notifications, inAppNotifications: true})}
              >
                Active
              </button>
              <button 
                className={`as-status-btn ${!notifications.inAppNotifications ? 'as-status-inactive' : ''}`}
                onClick={() => setNotifications({...notifications, inAppNotifications: false})}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>

        <button className="as-btn-save-preferences" onClick={handleSavePreferences}>
          Save Preferences
        </button>
      </div>
    </div>
  );

  return (
    <UserMasterLayout>
      <div className="as-container">
        <div className="as-header">
          <h1 className="as-main-title">Account Settings</h1>
          <p className="as-main-subtitle">Manage your account security, notifications, and preferences.</p>
        </div>

        <div className="as-tabs">
          {/* Security & Password tab - removed
          <button
            className={`as-tab ${activeTab === 'security' ? 'as-tab-active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <Shield size={18} />
            Security & Password
          </button> */}

          <button
            className={`as-tab ${activeTab === 'notifications' ? 'as-tab-active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <Bell size={18} />
            Notifications
          </button>

          {/* <button
            className={`as-tab ${activeTab === 'address' ? 'as-tab-active' : ''}`}
            onClick={() => setActiveTab('address')}
          >
            <ShoppingBag size={18} />
            Address
          </button> */}

          {/* Payments tab - commented out
          <button
            className={`as-tab ${activeTab === 'payments' ? 'as-tab-active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            <CreditCard size={18} />
            Payments
          </button> */}
        </div>

        <div className="as-content">
          {activeTab === 'security' && renderSecurityPassword()}
          {activeTab === 'notifications' && renderNotifications()}
          {activeTab === 'address' && (
            <div>
              <UserAddress />
            </div>
          )}
          {activeTab === 'payments' && (
            <div>
              <UserPayments />
            </div>
          )}
        </div>
      </div>
    </UserMasterLayout>
  );
};

export default UserAccountSettings;